// Shared jsPDF + autoTable generator for IPD billing documents (invoice bill + deposit
// receipt).
//
// The letterhead and the footer are the procurement PDF kit's
// (inventory/utils/pdfTheme.js) — the same ones the GRN and the PO print, so all four
// documents open and close identically. The bill's own body is unchanged; it only takes
// its colours from the kit's palette.
import jsPDF from "jspdf";
import "jspdf-autotable";
import {
  SIDE_M,
  CONT_TOP,
  FOOTER_RESERVE,
  C,
  drawLetterhead,
  drawContinuationHeader,
  stampFooters,
} from "../../inventory/utils/pdfTheme";
import { resolveFacilityIdentity, formatRegistrationLine } from "../../inventory/utils/facilityIdentity";

const HEADING_TEXT = C.navy;
const MUTED_TEXT = C.muted;
// Hairline rules, and the light tint behind a group header row.
const BAND = C.border;
const BAND_TEXT = C.navy;
const BAND_TINT = C.headBg;
const MARGIN = SIDE_M;
// ORIGINAL / DUPLICATE watermark — the same muted red the print sheet used.
const WATERMARK_TEXT = [220, 53, 69];
const WATERMARK_OPACITY = 0.12;
const WATERMARK_SIZE = 62;
const WATERMARK_ANGLE = 30;

const money = (v) =>
  (Number(v) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// Letterhead lines join their parts with a middle dot, skipping whatever is missing.
const joinDot = (parts) =>
  parts.map((p) => String(p ?? "").trim()).filter(Boolean).join(" · ");

// collateral_image (base64) from the BrandingContext array — the header logo.
function brandLogo(brandingData) {
  const it = Array.isArray(brandingData)
    ? brandingData.find((i) => i && i.name === "collateral_image")
    : null;
  return it && it.content ? `data:image/png;base64,${it.content}` : null;
}
function facilityName(fallback) {
  try {
    return sessionStorage.getItem("facility_display_name") || fallback || "Hospital";
  } catch (e) {
    return fallback || "Hospital";
  }
}

// Facility address for the letterhead — same fields in the same order the OPD invoice
// prints (billing-payment/ViewInvoice.js buildFacilityAddress) and the lab / radiology
// letterhead builds (utils/reportLetterhead.js addressFromUserInfo), off
// get_logged_in_user_info/. Empty when a session never loaded user info, in which case
// the header simply drops the line rather than printing a placeholder.
function facilityAddressLine(userInfo) {
  return joinDot([
    userInfo?.facility_address_line_1,
    userInfo?.facility_address_line_2,
    userInfo?.facility_address_line_3,
    userInfo?.city,
    userInfo?.state,
    userInfo?.pincode ? `PIN: ${userInfo.pincode}` : null,
  ]);
}

// The identity pdfTheme's letterhead expects.
//
// It starts from resolveFacilityIdentity() — what the GRN and PO letterheads print, i.e.
// the sessionStorage identity with the placeholder values filtered out — so a billing
// document falls back to the same header those two do. On top of that it prefers what
// the billing screen actually holds: `userInfo` from get_logged_in_user_info/ and the
// invoice's own hospital block, neither of which a plain module can reach.
//
// Only GSTIN has a field today; PAN and a facility email have no confirmed source
// (get_logged_in_user_info/ carries neither, and contactus@hlthtek.com is Hlthtek's own
// contact, not the facility's) — the tolerant lookup is here so they start printing the
// day the backend returns them. Anything missing is null and drops out of the line.
function billingIdentity(hospital, userInfo) {
  const base = resolveFacilityIdentity();
  return {
    name: facilityName(hospital?.name),
    address: facilityAddressLine(userInfo) || hospital?.address || base.address,
    gstin: hospital?.gstin || userInfo?.facility_gst_number || userInfo?.gst_number || base.gstin,
    pan: hospital?.pan || userInfo?.facility_pan || userInfo?.pan_number || base.pan,
    email: hospital?.email || userInfo?.facility_email || base.email,
  };
}

// ── Letterhead + footer: pdfTheme's, i.e. the GRN's ──────────────────────────
//
// Page 1 gets drawLetterhead (centred logo, facility name, address, GSTIN line, rule);
// pages 2+ get the compact drawContinuationHeader; every page gets the hairline footer
// from stampFooters, which is stamped last because it needs the final page count.
//
// Guard the continuation header so it runs at most once per page: both the explicit
// page break in COMBINED and autoTable's didDrawPage ask for it, and painting it twice
// visibly thickens the text.
function oncePerPage(doc, draw) {
  const done = new Set();
  return () => {
    const page = doc.internal.getCurrentPageInfo().pageNumber;
    if (done.has(page)) return;
    done.add(page);
    draw();
  };
}

// ORIGINAL / DUPLICATE as a large diagonal watermark across every page.
//
// PDF has no z-order — whatever is drawn last sits on top — and a page here is built by
// several separate autoTable calls, so there is no single "before the content" moment to
// paint behind. It is therefore stamped last, like the page numbers, and kept readable
// underneath by a low-opacity graphics state rather than by draw order.
function stampWatermark(doc, label) {
  const W = doc.internal.pageSize.getWidth();
  const H = doc.internal.pageSize.getHeight();
  const text = String(label).toUpperCase();
  doc.setFont("helvetica", "bold");
  doc.setFontSize(WATERMARK_SIZE);
  // jsPDF's align: "center" offsets by half the text width along x only, which leaves a
  // rotated stamp visibly off-centre — so the start of the baseline is placed by hand,
  // back down the 30° diagonal from the middle of the page.
  const rad = (WATERMARK_ANGLE * Math.PI) / 180;
  const half = doc.getTextWidth(text) / 2;
  const x = W / 2 - half * Math.cos(rad);
  const y = H / 2 + half * Math.sin(rad);
  const total = doc.internal.getNumberOfPages();
  for (let i = 1; i <= total; i += 1) {
    doc.setPage(i);
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity: WATERMARK_OPACITY }));
    doc.setFont("helvetica", "bold");
    doc.setFontSize(WATERMARK_SIZE);
    doc.setTextColor(...WATERMARK_TEXT);
    doc.text(text, x, y, { angle: WATERMARK_ANGLE });
    doc.restoreGraphicsState();
  }
  doc.setTextColor(0, 0, 0);
}

// Draw the page-1 letterhead, register the continuation header + content margins for
// autoTable pagination, and return the y the content starts at.
// `show = false` (pre-printed stationery) keeps every measurement and margin identical
// and only withholds the letterhead ink, so the content lands in the same place.
function withLetterhead(doc, identity, logo, onNewPage, show = true) {
  doc.autoTableSetDefaults({
    margin: { top: CONT_TOP, bottom: FOOTER_RESERVE + 4, left: MARGIN, right: MARGIN },
    // Fires for every page a table draws on; the guard skips the page the letterhead
    // is already on, and any page a previous table has already headed.
    didDrawPage: () => {
      if (doc.internal.getCurrentPageInfo().pageNumber > 1) onNewPage();
    },
  });
  return drawLetterhead(doc, identity, formatRegistrationLine(identity), logo, show);
}

// Two-column label:value info grid (borderless autoTable), returns finalY.
function infoGrid(doc, startY, pairs) {
  const body = [];
  for (let i = 0; i < pairs.length; i += 2) {
    const a = pairs[i], b = pairs[i + 1] || ["", ""];
    body.push([a[0], a[1] == null || a[1] === "" ? "—" : String(a[1]),
               b[0], b[1] == null || b[1] === "" ? "—" : String(b[1])]);
  }
  doc.autoTable({
    startY,
    body,
    theme: "plain",
    styles: { fontSize: 8, cellPadding: 0.8 },
    columnStyles: {
      0: { fontStyle: "bold", textColor: MUTED_TEXT, cellWidth: 32 },
      1: { cellWidth: 62 },
      2: { fontStyle: "bold", textColor: MUTED_TEXT, cellWidth: 32 },
      3: { cellWidth: 60 },
    },
  });
  return doc.lastAutoTable.finalY;
}

// Patient / bill info box — the IQ-style block the printed bill opens with: a centered
// title bar over a 4-column "Label : Value" grid, framed by a single outer border.
// Labels are black, values dark blue and colon-prefixed, and a long value (an address)
// wraps and grows its row. Same `pairs` shape as infoGrid.
function infoBox(doc, startY, title, pairs) {
  const body = [];
  const val = (v) => `: ${v == null || v === "" ? "—" : v}`;
  for (let i = 0; i < pairs.length; i += 2) {
    const a = pairs[i], b = pairs[i + 1];
    // An odd number of pairs leaves the right half of the last row with no field at
    // all — that cell stays blank rather than printing a stray ": —".
    body.push([a[0], val(a[1]), b ? b[0] : "", b ? val(b[1]) : ""]);
  }
  doc.autoTable({
    startY,
    head: [[{ content: String(title), colSpan: 4 }]],
    body,
    theme: "plain",
    // Outer frame only: cells draw no rules of their own, and tableLineWidth boxes the
    // whole block (and re-boxes it correctly if it ever splits across pages).
    tableLineColor: [0, 0, 0],
    tableLineWidth: 0.1,
    styles: {
      fontSize: 8.5,
      cellPadding: { top: 1.4, bottom: 1.4, left: 2, right: 2 },
      lineWidth: 0,
      textColor: HEADING_TEXT,
      valign: "middle",
    },
    headStyles: {
      halign: "center",
      fontSize: 11,
      fontStyle: "bold",
      textColor: [0, 0, 0],
      fillColor: false,
      cellPadding: 2,
    },
    columnStyles: {
      0: { fontStyle: "bold", textColor: [0, 0, 0], cellWidth: 38 },
      1: { cellWidth: 57 },
      2: { fontStyle: "bold", textColor: [0, 0, 0], cellWidth: 38 },
      3: { cellWidth: 57 },
    },
  });
  return doc.lastAutoTable.finalY;
}

// Shared look for the bill's data tables: no fills and no vertical rules — a small
// uppercase head over a dark underline, a hairline under each row, navy body text.
// Spread into an autoTable call, then add that table's own head/body/columnStyles.
const TABLE_BASE = {
  theme: "plain",
  styles: {
    fontSize: 8,
    cellPadding: { top: 1.6, bottom: 1.6, left: 2, right: 2 },
    textColor: HEADING_TEXT,
    lineColor: BAND,
    lineWidth: { bottom: 0.1 },
    valign: "middle",
  },
  headStyles: {
    fontSize: 7,
    fontStyle: "bold",
    textColor: MUTED_TEXT,
    lineColor: HEADING_TEXT,
    lineWidth: { bottom: 0.3 },
  },
};

function sectionTitle(doc, y, text) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...HEADING_TEXT);
  doc.text(String(text).toUpperCase(), MARGIN, y);
  doc.setTextColor(0, 0, 0);
  return y + 2;
}

// ── Group line items by department/category (same as InvoicePrintSheet) ──
function groupByCategory(lineItems) {
  const order = [], map = {};
  (lineItems || []).forEach((it) => {
    const key = it.dept || it.category || "Others";
    if (!map[key]) { map[key] = []; order.push(key); }
    map[key].push(it);
  });
  return order.map((k) => ({
    name: k,
    items: map[k],
    total: map[k].reduce((s, i) => s + (Number(i.amount) || 0), 0),
    discount: map[k].reduce((s, i) => s + (Number(i.discount) || 0), 0),
  }));
}

const lineNet = (it) => Number(it.amount || 0) - Number(it.discount || 0);

// ── The bill in the hospital's own sections ─────────────────────────────────
// The server arranges the invoice into the sections the bill format asks for - the package
// first, then bed, theatre, investigations, procedures, drugs, consultation - each with its
// subtotal, and theatre broken down by who was in the room. Drawing it is a matter of
// walking that structure; nothing here decides what belongs where, so the printed bill and
// the screen can never disagree about it.
const layoutMoney = (v) => money(Number(v || 0));

/** One line of an arranged section, as the detail table prints it. */
function layoutLineRow(line) {
  const name = line.service_name || line.service_code || "";
  // A pharmacy line carries its batch and expiry when the dispensing record has them.
  const extras = [line.batch_number ? `Batch ${line.batch_number}` : "",
                  line.expiry_date ? `Exp ${line.expiry_date}` : ""].filter(Boolean).join("  ");
  return [
    extras ? `${name}
${extras}` : name,
    line.quantity ?? "",
    layoutMoney(line.unit_price),
    layoutMoney(line.discount_amount),
    layoutMoney(line.net_amount),
  ];
}

/** A section, its blocks, and the classifications inside them - indented as it nests. */
function layoutSectionRows(section, body) {
  body.push([{ content: String(section.title || "").toUpperCase(), colSpan: 5,
               styles: { fontStyle: "bold", fillColor: BAND_TINT } }]);
  // A package whose price the invoice carries no line for still has to say what it costs.
  if (!(section.lines || []).length && section.package_price) {
    body.push(["Package price", "", "", "", layoutMoney(section.package_price)]);
  }
  const blocks = section.blocks || [];
  if (!blocks.length) {
    (section.lines || []).forEach((line) => body.push(layoutLineRow(line)));
  } else {
    blocks.forEach((block) => {
      body.push([{ content: `   ${block.title}`, colSpan: 5, styles: { fontStyle: "bold" } }]);
      const inner = block.blocks || [];
      if (inner.length) {
        inner.forEach((sub) => {
          body.push([{ content: `      ${sub.title}`, colSpan: 5,
                       styles: { textColor: [90, 90, 90] } }]);
          (sub.lines || []).forEach((line) => body.push(layoutLineRow(line)));
          body.push([{ content: `      ${sub.title} — Subtotal`, colSpan: 4,
                       styles: { halign: "right" } },
                     { content: layoutMoney(sub.totals?.net_amount), styles: { halign: "right" } }]);
        });
      } else {
        (block.lines || []).forEach((line) => body.push(layoutLineRow(line)));
      }
      body.push([{ content: `   ${block.title} — Subtotal`, colSpan: 4,
                   styles: { fontStyle: "bold", halign: "right" } },
                 { content: layoutMoney(block.totals?.net_amount),
                   styles: { fontStyle: "bold", halign: "right" } }]);
    });
  }
  body.push([{ content: `${section.title} — Total`, colSpan: 4,
               styles: { fontStyle: "bold" } },
             { content: layoutMoney(section.totals?.net_amount),
               styles: { fontStyle: "bold", halign: "right" } }]);
}

/**
 * Build the IPD bill PDF and return the jsPDF doc.
 *
 * `format` picks how much detail the bill carries (PrintOptionsModal step 2):
 *   SUMMARY   — category totals + settlement only
 *   DETAILED  — every line item grouped by category, with the same settlement block
 *   COMBINED  — the summary bill, then the detailed bill on a fresh page (default)
 * `letterhead = false` leaves the header/footer bands off for pre-printed stationery.
 */
export function generateInvoicePdf({ invoice, brandingData, userInfo, label = "", format = "COMBINED", letterhead = true }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const hosp = invoice.hospital || {};
  const identity = billingIdentity(hosp, userInfo);
  const logo = brandLogo(brandingData);
  const W = doc.internal.pageSize.getWidth();
  const fmt = String(format || "COMBINED").toUpperCase();

  const h = invoice.bill_header || {};
  const p = invoice.patient || {};

  // A DRAFT invoice hasn't been issued yet, so what it prints is an interim bill, not the
  // final settlement. `invoice_status` is the raw code (billing-api INVOICE_STATUS_LABEL);
  // `status` may be the backend's effective_status, so it is only the fallback.
  const billKind =
    String(invoice.invoice_status || invoice.status || "").toUpperCase() === "DRAFT"
      ? "INTERIM BILL"
      : "FINAL BILL";

  const onNewPage = oncePerPage(doc, () =>
    drawContinuationHeader(doc, identity, billKind, invoice.invoice_no, logo, letterhead));
  const contentTop = withLetterhead(doc, identity, logo, onNewPage, letterhead) + 8;

  // Category summary. Exclude discount / adjustment lines (negative amounts — e.g. an
  // adhoc discount posted under a "BILLING" category) from the service rows; they are
  // reflected in the Discount total, not shown as a service line.
  const groups = groupByCategory((invoice.line_items || []).filter((it) => Number(it.amount) >= 0));
  const totalService = groups.reduce((s, g) => s + g.total, 0);

  const approved = Number(invoice.insurance_approved) || 0;
  const totalBill = Number(invoice.total_payable) || totalService;
  const payerName = invoice.insurance_payer_name || (invoice.payer && invoice.payer.name) || "Payer";
  // Patient's share of the bill (before their own deposits/payments).
  const netPayable = Math.max(0, totalBill - approved);
  // Amount the patient still owes. In the co-pay case use the account-derived figure
  // (patient_payable_amt = net − insurance − deposits − paid, each counted once); otherwise
  // net share − money received. `paid_amount` already includes an adjusted deposit, so it is
  // NOT added to deposits_adjusted here (that would double-count: 1260 − 500 − 500 = 260).
  const received = Number(invoice.paid_amount) || 0;
  const outstanding = approved > 0 && invoice.patient_payable_amt != null
    ? Math.max(0, Number(invoice.patient_payable_amt))
    : Math.max(0, netPayable - received);
  const receiptShown = Math.max(0, netPayable - outstanding);
  // Discount = services − net bill (reconciles the three rows and picks up the negative
  // discount line excluded above), falling back to the invoice's own discount field.
  const discountAmt = Math.max(0, totalService - totalBill) || Math.abs(Number(invoice.discount) || 0);

  // A bill covering part of an admission says which part, under the title: a package patient
  // handed two sheets has to be able to tell them apart, and each shows a total that is not
  // the admission's total. Without the line, either sheet reads like a bill missing charges.
  const scopeCaption = {
    PACKAGE_ONLY: "Package charges only - services outside the package are billed separately",
    OUT_OF_PACKAGE: "Services outside the package only - the package is billed separately",
  }[String(invoice.invoice_scope || "ALL").toUpperCase()] || "";

  const billTitle = (y, text) => {
    doc.setFont("helvetica", "bold"); doc.setFontSize(12);
    doc.text(text, W / 2, y, { align: "center" });
    if (!scopeCaption) return y + 6;
    doc.setFont("helvetica", "normal"); doc.setFontSize(8);
    doc.setTextColor(90, 90, 90);
    doc.text(scopeCaption, W / 2, y + 4.5, { align: "center" });
    doc.setTextColor(0, 0, 0);
    return y + 10;
  };

  // Patient / bill info box, titled with the bill it heads.
  const drawMeta = (y, title) => infoBox(doc, y, title, [
    ["Bill No.", invoice.invoice_no], ["Bill Date", invoice.issued_date],
    ["UHID", p.uhid], ["Age / Gender", [p.age, p.gender].filter((x) => x && x !== "—").join(" / ")],
    ["Patient", p.name], ["Category", h.patient_category],
    ["Payer", invoice.payer && invoice.payer.name], ["Phone", h.phone],
    [h.admission_label || "Admission Date Time", h.admission_date], ["Ward / Bed", h.ward_bed],
    [h.discharge_label || "Discharge", h.discharge_date], ["Consultant", invoice.doctor && invoice.doctor.name],
    ["Department", invoice.doctor && invoice.doctor.specialty], ["Status", invoice.status],
    ["Encounter ID", invoice.encounter_id],
  ]) + 4;

  // One row per section with its total - the same sections the detailed bill prints, so the
  // summary and the detail are two views of one arrangement rather than two groupings.
  const drawCategoryTable = (y) => {
    const sections = (invoice.bill_layout && invoice.bill_layout.sections) || [];
    const summaryBody = sections.length
      ? sections.map((section, i) => [
          i + 1,
          section.title,
          layoutMoney(section.totals?.discount_amount),
          layoutMoney(section.package_price || section.totals?.net_amount),
        ])
      : groups.map((g, i) => [i + 1, g.name, money(g.discount), money(g.total - g.discount)]);
    doc.autoTable({
      startY: y,
      ...TABLE_BASE,
      head: [["#", "Service / Package Name", "Discount", "Amount"]],
      body: summaryBody.length ? summaryBody : [["", "No services", "", ""]],
      columnStyles: {
        0: { cellWidth: 12 },
        2: { halign: "right" },
        3: { halign: "right", fontStyle: "bold" },
      },
    });
    return doc.lastAutoTable.finalY + 4;
  };

  // Every line item, in the hospital's own sections when the server arranged them, and
  // grouped flat by category when it did not - an older server still prints a bill.
  const drawDetailTable = (y) => {
    const detailBody = [];
    const sections = (invoice.bill_layout && invoice.bill_layout.sections) || [];
    if (sections.length) {
      sections.forEach((section) => layoutSectionRows(section, detailBody));
      doc.autoTable({
        startY: y,
        ...TABLE_BASE,
        head: [["Service / Package Name", "Qty", "Rate", "Discount", "Amount"]],
        body: detailBody,
        columnStyles: {
          1: { cellWidth: 14, halign: "right" },
          2: { halign: "right" },
          3: { halign: "right" },
          4: { halign: "right", fontStyle: "bold" },
        },
      });
      return doc.lastAutoTable.finalY + 4;
    }
    groups.forEach((g) => {
      detailBody.push([{ content: g.name, colSpan: 5, styles: { fontStyle: "bold", fillColor: BAND_TINT } }]);
      g.items.forEach((it) => {
        // Scheme/corporate line: alias name, plus its scheme code on a second line.
        const nameCell = it.isScheme && it.service_code ? `${it.description}\n${it.service_code}` : it.description;
        detailBody.push([nameCell, it.qty, money(it.rate), money(it.discount || 0), money(lineNet(it))]);
      });
      detailBody.push([{ content: `${g.name} — Subtotal`, colSpan: 4, styles: { fontStyle: "bold" } },
        { content: money(g.total - g.discount), styles: { fontStyle: "bold", halign: "right" } }]);
    });
    doc.autoTable({
      startY: y,
      ...TABLE_BASE,
      head: [["Service / Package Name", "Qty", "Rate", "Discount", "Amount"]],
      body: detailBody.length ? detailBody : [["No line items", "", "", "", ""]],
      columnStyles: {
        1: { cellWidth: 14, halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "right", fontStyle: "bold" },
      },
    });
    return doc.lastAutoTable.finalY + 4;
  };

  // Totals + settlement matrix + LOA / deposits / payments.
  const drawSettlement = (y) => {
    // Totals block (right aligned)
    const totalsRows = [
      ["Total Service Amount", money(totalService)],
      ["Discount", money(discountAmt)],
      ["Total Bill Amount", money(totalBill)],
    ];
    if (approved > 0) totalsRows.push([`Payable By Others (${payerName})`, money(approved)]);
    totalsRows.push([approved > 0 ? "Net Payable (Patient)" : "Net Payable", money(netPayable)]);
    totalsRows.push(["Receipt", money(receiptShown)]);
    totalsRows.push(["Bill Outstanding", money(outstanding)]);
    doc.autoTable({
      startY: y,
      body: totalsRows,
      theme: "plain",
      tableWidth: 82,
      margin: { left: W - MARGIN - 82 },
      styles: { fontSize: 9, cellPadding: 1 },
      columnStyles: { 0: { cellWidth: 52 }, 1: { halign: "right", fontStyle: "bold" } },
    });
    y = doc.lastAutoTable.finalY + 5;

    // Settlement matrix. `paid` already includes any adjusted deposit, so the Receipt column
    // shows only the non-deposit payments (paid − deposit) — otherwise the deposit is counted
    // in both the Deposit and Receipt columns. Balance = patient bill − everything received.
    const patientBill = Math.max(0, totalBill - approved);
    const dep = Number(invoice.deposits_adjusted) || 0;
    const paid = Number(invoice.paid_amount) || 0;
    const receiptNonDep = Math.max(0, paid - dep);
    const patientBal = Math.max(0, patientBill - paid);
    y = sectionTitle(doc, y, "Payer / Patient Settlement") + 1;
    const matrix = [];
    if (approved > 0) matrix.push([payerName, money(approved), "0.00", "0.00", "0.00", money(approved)]);
    matrix.push(["Patient", money(patientBill), money(dep), money(receiptNonDep), "0.00", money(patientBal)]);
    matrix.push(["Total", money(totalBill), money(dep), money(receiptNonDep), "0.00", money(approved + patientBal)]);
    doc.autoTable({
      startY: y,
      ...TABLE_BASE,
      head: [["Payer / Patient", "Bill Amt", "Deposit", "Receipt", "Refund", "Balance"]],
      body: matrix,
      columnStyles: {
        1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" },
        4: { halign: "right" }, 5: { halign: "right", fontStyle: "bold" },
      },
    });
    y = doc.lastAutoTable.finalY + 4;

    // LOA
    if (approved > 0) {
      y = sectionTitle(doc, y, "LOA Details") + 1;
      doc.autoTable({
        ...TABLE_BASE,
        startY: y,
        head: [["TPA / Payer", "Approved / LOA Amount"]],
        body: [[payerName, money(approved)]],
        columnStyles: { 1: { halign: "right", fontStyle: "bold" } },
      });
      y = doc.lastAutoTable.finalY + 4;
    }

    // Deposits and payments as one receipt ledger. The two carry different fields —
    // a deposit has utilized / balance, a payment has a mode — so each row leaves the
    // other's columns blank and the Type column says which it is.
    const deposits = invoice.deposits || [];
    const payments = invoice.payments || [];
    if (deposits.length || payments.length) {
      // Extra lead-in so the heading isn't crowded by the table above it.
      y = sectionTitle(doc, y + 6, "Payment Receipt") + 1;
      doc.autoTable({
        ...TABLE_BASE,
        startY: y,
        head: [["Receipt No.", "Date", "Type", "Mode", "Amount", "Utilized", "Balance"]],
        body: [
          ...deposits.map((d) => [
            d.receipt_no, d.date, "Deposit", d.mode || "—", money(d.amount), money(d.utilized), money(d.balance),
          ]),
          ...payments.map((pmt) => [
            pmt.number, pmt.date, "Payment", pmt.mode, money(pmt.amount), "—", "—",
          ]),
        ],
        columnStyles: {
          4: { halign: "right", fontStyle: "bold" },
          5: { halign: "right" },
          6: { halign: "right" },
        },
      });
      y = doc.lastAutoTable.finalY + 4;
    }
    return y;
  };

  let y = contentTop;
  // Summary bill — SUMMARY and COMBINED.
  if (fmt !== "DETAILED") {
    y = drawMeta(y, `${billKind} (SUMMARY)`);
    y = drawCategoryTable(y);
    y = drawSettlement(y);
  }
  // Detail bill — DETAILED and COMBINED. On COMBINED it follows the summary on a new
  // page and skips the meta/settlement blocks the summary already carries; on its own
  // it has to stand alone as a complete bill.
  if (fmt !== "SUMMARY") {
    // A fresh page carries the continuation header, not the letterhead, so the detail
    // bill starts at the continued-page content top.
    if (fmt === "COMBINED") { doc.addPage(); onNewPage(); y = CONT_TOP; }
    // On COMBINED the summary page already carries the info box, so the detail page
    // only needs the title over its line items.
    y = fmt === "DETAILED"
      ? drawMeta(y, `${billKind} (DETAIL)`)
      : billTitle(y, `${billKind} (DETAIL)`);
    y = drawDetailTable(y);
    if (fmt === "DETAILED") y = drawSettlement(y);
  }
  if (label) stampWatermark(doc, label);
  stampFooters(doc, identity, letterhead);
  return doc;
}

/**
 * Build the deposit (advance payment) receipt PDF and return the jsPDF doc.
 * `letterhead = false` leaves the header/footer bands off for pre-printed stationery.
 */
export function generateDepositReceiptPdf({ receipt, brandingData, userInfo, letterhead = true }) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const hosp = receipt.hospital || {};
  const pat = receipt.patient || {};
  const adm = receipt.admission || {};
  const dep = receipt.deposit || {};
  const wal = receipt.wallet || {};
  const identity = billingIdentity(hosp, userInfo);
  const logo = brandLogo(brandingData);
  const onNewPage = oncePerPage(doc, () =>
    drawContinuationHeader(doc, identity, "Payment Receipt", dep.receipt_no, logo, letterhead));
  const headerH = withLetterhead(doc, identity, logo, onNewPage, letterhead);
  const W = doc.internal.pageSize.getWidth();

  let y = headerH + 8;
  doc.setFont("helvetica", "bold"); doc.setFontSize(13);
  doc.text("PAYMENT RECEIPT", W / 2, y, { align: "center" });
  doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text(`Receipt No: ${dep.receipt_no || "—"}`, MARGIN, (y += 7));
  doc.text(`Date: ${dep.deposit_date ? new Date(dep.deposit_date).toLocaleString("en-IN") : "—"}`, W - MARGIN, y, { align: "right" });
  y += 3;

  y = infoGrid(doc, y, [
    ["Received From", pat.name], ["UHID", pat.uhid],
    ["Age / Gender", [pat.age, pat.gender].filter(Boolean).join("/")], ["Phone", pat.phone],
    ["Admission", `#${adm.admission_no || adm.admission_id || "—"}`],
    ["Bed", [adm.bed, adm.bed_type].filter(Boolean).join(" / ")],
  ]) + 6;

  // Amount box
  doc.setDrawColor(...BAND); doc.setLineWidth(0.4);
  doc.rect(MARGIN, y, W - 2 * MARGIN, 16);
  doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(...MUTED_TEXT);
  doc.text("AMOUNT RECEIVED", MARGIN + 3, y + 6);
  doc.setTextColor(0, 0, 0); doc.setFontSize(18);
  doc.text(`INR ${money(dep.amount)}`, W - MARGIN - 3, y + 8, { align: "right" });
  doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(...MUTED_TEXT);
  doc.text(String(dep.amount_in_words || ""), MARGIN + 3, y + 13);
  doc.setTextColor(0, 0, 0);
  y += 22;

  y = infoGrid(doc, y, [
    ["Payment Mode", dep.payment_mode], ["Reference", dep.transaction_reference],
    ["Collected By", dep.collected_by], ["Remarks", dep.remarks],
  ]) + 5;

  y = sectionTitle(doc, y, "Deposit Wallet") + 1;
  doc.autoTable({
    startY: y, theme: "plain",
    body: [
      ["Total Deposits", money(wal.total_deposits)],
      ["Adjusted to Bills", money(wal.adjusted)],
      ["Available Balance", money(wal.available_balance)],
    ],
    styles: { fontSize: 9, cellPadding: 1 },
    columnStyles: { 0: { cellWidth: 60 }, 1: { halign: "right", fontStyle: "bold" } },
    tableWidth: 100,
  });
  stampFooters(doc, identity, letterhead);
  return doc;
}

/**
 * Open the generated PDF in a new tab for print/preview.
 *
 * The blob is shown inside an iframe rather than by navigating the tab straight at the
 * blob URL. A top-level navigation to a PDF is what Chrome's "Download PDFs instead of
 * automatically opening them in Chrome" setting intercepts — the tab closes itself and
 * the file lands in Downloads, which is why print appeared to download directly. A PDF
 * embedded in a page is not intercepted and renders in the built-in viewer.
 *
 * The document is fully built before the tab is opened. If window.open is refused
 * outright (popup blocker), fall back to downloading so the bill is never lost.
 */
export function openPdf(doc, filename) {
  const name = filename || "document.pdf";
  try {
    const url = `${doc.output("bloburl")}#zoom=page-fit`;
    const win = window.open("", "_blank");
    if (!win || win.closed || typeof win.closed === "undefined") {
      doc.save(name);
      return;
    }
    win.document.write(
      "<!doctype html><html><head><title>" + name + "</title>" +
      "<style>html,body{margin:0;padding:0;height:100%;background:#525659}" +
      "iframe{border:0;width:100%;height:100%;display:block}</style></head>" +
      '<body><iframe src="' + url + '"></iframe></body></html>'
    );
    win.document.close();
  } catch (e) {
    doc.save(name);
  }
}
/**
 * Build the doctor payout statement PDF and return the jsPDF doc.
 *
 * `statement` is what the EMR statement endpoint returns: a date range, a list of doctors
 * each carrying their own lines and totals, and a grand total across all of them.
 *
 * Two shapes from one generator, because they are the same document read by two people:
 *   CONSOLIDATED — every doctor, each on their own page, then the hospital's grand total.
 *   INDIVIDUAL   — the one doctor in `statement.doctors`, which is what they are handed.
 *
 * Every line carries what it was paid for and why: the service, the group and classification
 * it sits under, the department, the rule that paid it, what the patient was discounted, and
 * the TDS withheld. A payout a doctor cannot check line by line is one they will ask about.
 *
 * `letterhead = false` leaves the header/footer bands off for pre-printed stationery.
 */
export function generateDoctorPayoutPdf({
  statement,
  brandingData,
  userInfo,
  mode = "CONSOLIDATED",
  letterhead = true,
}) {
  const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
  const identity = billingIdentity({}, userInfo);
  const logo = brandLogo(brandingData);
  const W = doc.internal.pageSize.getWidth();
  const consolidated = String(mode || "CONSOLIDATED").toUpperCase() !== "INDIVIDUAL";
  const doctors = statement.doctors || [];
  const totals = statement.totals || {};
  const title = consolidated ? "DOCTOR PAYOUT STATEMENT" : "DOCTOR PAYOUT STATEMENT";
  const period = `${statement.from_date || "—"} to ${statement.to_date || "—"}`;

  const onNewPage = oncePerPage(doc, () =>
    drawContinuationHeader(doc, identity, title, period, logo, letterhead));
  let y = withLetterhead(doc, identity, logo, onNewPage, letterhead) + 8;

  const centreTitle = (at, text, size = 12) => {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(size);
    doc.setTextColor(0, 0, 0);
    doc.text(String(text), W / 2, at, { align: "center" });
    return at + 6;
  };

  // A doctor's block: who they are, then every line they earned on, then their totals.
  const drawDoctor = (startY, d) => {
    let at = infoBox(doc, startY, d.provider_name || "—", [
      ["Practitioner", d.provider_name],
      ["Specialisation", d.specialization],
      ["Practitioner Code", d.provider_code],
      ["Period", period],
      ["Payout Lines", String(d.line_count ?? (d.lines || []).length)],
      ["Net Payable", money(d.net_payout_amount)],
    ]) + 5;

    doc.autoTable({
      startY: at,
      ...TABLE_BASE,
      head: [["Date", "Patient", "Encounter", "Department", "Service", "Group", "Classification",
              "Rule", "Charge", "Discount", "Basis", "Rate", "Payout", "TDS", "Net"]],
      body: (d.lines || []).map((ln) => [
        ln.date || "",
        ln.patient_name || "",
        [ln.encounter_number, ln.encounter_type].filter(Boolean).join(" · "),
        ln.department || "—",
        [ln.service_name, ln.service_code].filter(Boolean).join("\n"),
        ln.service_group || "—",
        ln.service_classification || "—",
        [ln.rule_name || ln.rule_code, (ln.payout_role || "").replace(/_/g, " ")].filter(Boolean).join("\n"),
        money(ln.charge_amount),
        ln.discount_amount ? `-${money(ln.discount_amount)}` : "—",
        money(ln.basis_amount),
        ln.payout_type === "PERCENTAGE" ? `${money(ln.payout_value)}%` : money(ln.payout_value),
        money(ln.gross_payout_amount),
        ln.tds_amount ? `-${money(ln.tds_amount)}` : "—",
        money(ln.net_payout_amount),
      ]),
      foot: [[
        { content: "Total", colSpan: 8, styles: { halign: "right", fontStyle: "bold" } },
        money(d.charge_amount),
        d.discount_amount ? `-${money(d.discount_amount)}` : "—",
        "", "",
        money(d.gross_payout_amount),
        d.tds_amount ? `-${money(d.tds_amount)}` : "—",
        money(d.net_payout_amount),
      ]],
      footStyles: {
        fontStyle: "bold", textColor: BAND_TEXT, fillColor: BAND_TINT,
        lineColor: HEADING_TEXT, lineWidth: { top: 0.3 },
      },
      columnStyles: {
        8: { halign: "right" }, 9: { halign: "right" }, 10: { halign: "right" },
        11: { halign: "right" }, 12: { halign: "right" }, 13: { halign: "right" },
        14: { halign: "right", fontStyle: "bold" },
      },
      styles: { ...TABLE_BASE.styles, fontSize: 7 },
      margin: { left: MARGIN, right: MARGIN },
    });
    return doc.lastAutoTable.finalY + 6;
  };

  y = centreTitle(y, consolidated ? "CONSOLIDATED DOCTOR PAYOUT" : "DOCTOR PAYOUT STATEMENT");
  y = centreTitle(y, period, 9);

  if (doctors.length === 0) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(...MUTED_TEXT);
    doc.text("No payouts in this period.", W / 2, y + 10, { align: "center" });
    stampFooters(doc, identity, letterhead);
    return doc;
  }

  doctors.forEach((d, index) => {
    // One doctor per page: a statement that runs two doctors together cannot be torn off
    // and handed to either of them.
    if (index > 0) { doc.addPage(); onNewPage(); y = CONT_TOP; }
    y = drawDoctor(y, d);
  });

  // The hospital's grand total, on its own page, and only when there is more than one
  // doctor to add up.
  if (consolidated && doctors.length > 1) {
    doc.addPage();
    onNewPage();
    y = CONT_TOP;
    y = centreTitle(y, "ALL PRACTITIONERS — SUMMARY");
    y = centreTitle(y, period, 9) + 2;
    doc.autoTable({
      startY: y,
      ...TABLE_BASE,
      head: [["Practitioner", "Specialisation", "Lines", "Charges", "Discount", "Payout", "TDS", "Net Payable"]],
      body: doctors.map((d) => [
        d.provider_name || "—",
        d.specialization || "—",
        String(d.line_count ?? (d.lines || []).length),
        money(d.charge_amount),
        d.discount_amount ? `-${money(d.discount_amount)}` : "—",
        money(d.gross_payout_amount),
        d.tds_amount ? `-${money(d.tds_amount)}` : "—",
        money(d.net_payout_amount),
      ]),
      foot: [[
        { content: `${totals.doctors ?? doctors.length} practitioners`, colSpan: 2, styles: { fontStyle: "bold" } },
        // columnStyles only reach body rows in autotable, so the foot figures
        // carry their own right alignment to line up with the column above.
        { content: String(totals.lines ?? ""), styles: { halign: "right" } },
        { content: money(totals.charge_amount), styles: { halign: "right" } },
        { content: totals.discount_amount ? `-${money(totals.discount_amount)}` : "—", styles: { halign: "right" } },
        { content: money(totals.gross_payout_amount), styles: { halign: "right" } },
        { content: totals.tds_amount ? `-${money(totals.tds_amount)}` : "—", styles: { halign: "right" } },
        { content: money(totals.net_payout_amount), styles: { halign: "right" } },
      ]],
      footStyles: {
        fontStyle: "bold", textColor: BAND_TEXT, fillColor: BAND_TINT,
        lineColor: HEADING_TEXT, lineWidth: { top: 0.3 },
      },
      columnStyles: {
        2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" },
        5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right", fontStyle: "bold" },
      },
      margin: { left: MARGIN, right: MARGIN },
    });
  }

  stampFooters(doc, identity, letterhead);
  return doc;
}

/** Download the generated PDF. */
export function savePdf(doc, filename) {
  doc.save(filename || "document.pdf");
}
